import React, { useEffect, useState } from "react";
import { brandIsNetwork } from "../../../utils/plans";
import { Box, Typography } from "@mui/material";
import { useOutletContext } from "react-router-dom";
import { TabHeader } from "../../../components/Tabs/TabHeader";
import { IBrandDetailsContext } from "../SingleBrandDetails";
import { NetworkBrandsPanel } from "./NetworkBrandsPanel";
import { WhiteLabelSettingsPanel } from "./WhiteLabelSettingsPanel";
import { CrawlPanel } from "./CrawlPanel";
import { CategoryTreePanel } from "./CategoryTreePanel";
import { DiscoveredReviewPanel } from "./DiscoveredReviewPanel";

// Stable tab ids — the list is filtered per brand, so values must not shift
// with position (Network Brands is RMN-only; everything else is open to any
// brand since TDC-1499 phase 2).
const TAB_NETWORK_BRANDS = 0;
const TAB_WHITE_LABEL = 1;
const TAB_CRAWL = 2;
const TAB_CATEGORIES = 3;
const TAB_REVIEW = 4;

// Network/crawl management tab for a brand (brands/:id/rmn, internal-only).
// Open to every brand: White Label (allowed_domains — the crawl
// prerequisite — plus theming for RMN portals), Crawl, Categories and
// Review; the Network Brands association editor only applies to rmn:true
// brands.
export default function BrandRmnPage() {
    const { brand } = useOutletContext<IBrandDetailsContext>();
    // The outlet seeds `brand` with a placeholder ({ name: '' }) until the
    // fetch resolves, so gate on the id rather than the object.
    const brandId = brand?.id as number | undefined;
    // Network root (plan enterprise_rmn | enterprise_agency; the API's derived rmn).
    const isRmn = brandIsNetwork(brand as any);
    const [tab, setTab] = useState(TAB_WHITE_LABEL);

    // Pick the default tab once the brand (and its plan) is known —
    // Network Brands for network plans, White Label otherwise.
    useEffect(() => {
        if (!brandId) return;
        setTab(isRmn ? TAB_NETWORK_BRANDS : TAB_WHITE_LABEL);
    }, [brandId, isRmn]);

    if (!brandId) {
        return (
            <Typography variant="body1" sx={{ opacity: 0.7 }}>
                Loading brand…
            </Typography>
        );
    }

    const tabs = [
        ...(isRmn ? [{ value: TAB_NETWORK_BRANDS, text: "Network Brands" }] : []),
        { value: TAB_WHITE_LABEL, text: "White Label" },
        { value: TAB_CRAWL, text: "Crawl" },
        { value: TAB_CATEGORIES, text: "Categories" },
        { value: TAB_REVIEW, text: "Discovered Brands & Products" },
    ];

    return (
        <Box className="flex flex-col gap-4">
            <TabHeader
                tabs={tabs}
                selectedTab={tab}
                setSelectedTab={setTab}
                variant="underline"
            />
            {tab === TAB_NETWORK_BRANDS && isRmn && <NetworkBrandsPanel />}
            {tab === TAB_WHITE_LABEL && <WhiteLabelSettingsPanel />}
            {tab === TAB_CRAWL && <CrawlPanel brandId={brandId} />}
            {tab === TAB_CATEGORIES && <CategoryTreePanel brandId={brandId} />}
            {tab === TAB_REVIEW && <DiscoveredReviewPanel brandId={brandId} />}
        </Box>
    );
}
